/**
 * Input sanitization utilities (no external dependencies).
 *
 * Only sanitizeText is used in production (API routes strip HTML from
 * user-submitted emails/names). sanitizeHTML and sanitizeMarkdown are
 * kept for future use with a whitelist-based approach.
 */

const DANGEROUS_CONTENT_TAGS =
  /<(script|style|noscript|iframe|object|embed|applet|form)[^>]*>[\s\S]*?<\/\1>/gi

const SELF_CLOSING_DANGEROUS =
  /<(script|style|noscript|iframe|object|embed|applet|form)[^>]*\/?>/gi

const ALL_TAGS = /<[^>]*>/g

// Allowed tags for sanitizeHTML
const HTML_SAFE_TAGS = new Set([
  'b',
  'i',
  'em',
  'strong',
  'a',
  'p',
  'br',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'code',
  'pre',
])

// Allowed tags for sanitizeMarkdown (superset of HTML_SAFE_TAGS)
const MARKDOWN_SAFE_TAGS = new Set([
  ...HTML_SAFE_TAGS,
  'hr',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'img',
])

const HTML_SAFE_ATTRS = new Set(['href', 'target', 'rel', 'class'])
const MARKDOWN_SAFE_ATTRS = new Set([
  'href',
  'target',
  'rel',
  'class',
  'src',
  'alt',
  'title',
])

const DANGEROUS_URI = /^javascript:/i

/**
 * Strip a tag down to only allowed attributes.
 */
function cleanTag(tag: string, allowedAttrs: Set<string>): string {
  // Extract tag name
  const nameMatch = tag.match(/^<\/?([a-zA-Z][a-zA-Z0-9]*)\s*/i)
  if (!nameMatch) return ''
  const tagName = nameMatch[1].toLowerCase()
  const isClosing = tag.startsWith('</')

  if (isClosing) return `</${tagName}>`

  // Extract and filter attributes
  const attrs: string[] = []
  const attrRegex =
    /([a-zA-Z][a-zA-Z0-9-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|(\S+)))?/g
  // Skip past the tag name
  const attrString = tag.slice(nameMatch[0].length)
  let match
  while ((match = attrRegex.exec(attrString)) !== null) {
    const attrName = match[1].toLowerCase()
    if (attrName === tagName) continue // Skip tag name false match at end
    const attrValue = match[2] ?? match[3] ?? match[4] ?? ''
    if (!allowedAttrs.has(attrName)) continue
    if (attrName === 'href' && DANGEROUS_URI.test(attrValue.trim())) continue
    attrs.push(`${attrName}="${attrValue}"`)
  }

  const isSelfClosing =
    tag.endsWith('/>') || ['br', 'hr', 'img'].includes(tagName)
  const suffix = isSelfClosing ? ' />' : '>'
  return attrs.length > 0
    ? `<${tagName} ${attrs.join(' ')}${suffix}`
    : `<${tagName}${suffix}`
}

/**
 * Whitelist-based HTML sanitizer.
 * Removes dangerous tags and their content, strips disallowed tags,
 * filters attributes on allowed tags.
 */
function whitelistSanitize(
  input: string,
  allowedTags: Set<string>,
  allowedAttrs: Set<string>
): string {
  // Remove dangerous content-bearing elements first
  let clean = input.replace(DANGEROUS_CONTENT_TAGS, '')
  clean = clean.replace(SELF_CLOSING_DANGEROUS, '')

  // Process remaining tags: keep allowed, strip disallowed
  clean = clean.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*\/?>/g, fullMatch => {
    const nameMatch = fullMatch.match(/^<\/?([a-zA-Z][a-zA-Z0-9]*)/i)
    if (!nameMatch) return ''
    const tagName = nameMatch[1].toLowerCase()
    if (!allowedTags.has(tagName)) return ''
    return cleanTag(fullMatch, allowedAttrs)
  })

  return clean
}

/**
 * Sanitize HTML content to prevent XSS attacks.
 * Allows a safe subset of HTML tags and attributes.
 */
export function sanitizeHTML(dirty: string): string {
  return whitelistSanitize(dirty, HTML_SAFE_TAGS, HTML_SAFE_ATTRS)
}

/**
 * Sanitize plain text (removes all HTML).
 * Strips dangerous element content (script, style, etc.) then removes all tags.
 */
export function sanitizeText(text: string): string {
  if (!text) return ''
  let clean = text.replace(DANGEROUS_CONTENT_TAGS, '')
  clean = clean.replace(SELF_CLOSING_DANGEROUS, '')
  clean = clean.replace(ALL_TAGS, '')
  return clean
}

/**
 * Sanitize markdown content (more permissive, allows tables and images).
 */
export function sanitizeMarkdown(markdown: string): string {
  return whitelistSanitize(markdown, MARKDOWN_SAFE_TAGS, MARKDOWN_SAFE_ATTRS)
}

// ── URL validation (SSRF prevention) ──────────────────────────────────────────

/**
 * Hostnames that must never be fetched regardless of what DNS says.
 *
 * The cloud metadata names are here as defence in depth: outside the cloud
 * they usually fail to resolve (and we fail closed anyway), but inside it
 * they hand out credentials. RFC 6761 reserves `localhost` and everything
 * under `.localhost` for the loopback interface.
 */
const BLOCKED_HOSTNAMES = new Set([
  'localhost',
  'metadata',
  'metadata.google.internal',
  'metadata.goog',
  'instance-data',
])

/** Only these ports may be fetched. '' is the scheme's implicit default. */
const ALLOWED_PORTS = new Set(['', '80', '443'])

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:'])

/** `URL.hostname` keeps the brackets on IPv6 literals; every check needs them off. */
function stripBrackets(host: string): string {
  return host.startsWith('[') && host.endsWith(']') ? host.slice(1, -1) : host
}

/** Parse a dotted-quad into 4 bytes, or null if it is not one. */
function parseIpv4(input: string): number[] | null {
  const parts = input.split('.')
  if (parts.length !== 4) return null
  const bytes: number[] = []
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part)) return null
    const n = Number(part)
    if (n > 255) return null
    bytes.push(n)
  }
  return bytes
}

/**
 * Parse any IPv6 textual form into 16 bytes, or null if it is not one.
 *
 * Handles `::` compression, a trailing embedded IPv4 (`::ffff:127.0.0.1`),
 * and zone ids (`fe80::1%eth0`). Written by hand because the whole point of
 * this module is zero third-party parsing of untrusted input.
 */
function parseIpv6(input: string): number[] | null {
  let text = input
  const zone = text.indexOf('%')
  if (zone !== -1) text = text.slice(0, zone)
  if (text.length === 0 || text.length > 45) return null

  // A trailing dotted-quad occupies the last two 16-bit groups.
  let embedded: number[] | null = null
  if (text.includes('.')) {
    const cut = text.lastIndexOf(':')
    if (cut === -1) return null
    embedded = parseIpv4(text.slice(cut + 1))
    if (!embedded) return null
    text = text.slice(0, cut)
  }

  const halves = text.split('::')
  if (halves.length > 2) return null

  const toGroups = (part: string): number[] | null => {
    if (part === '') return []
    const out: number[] = []
    for (const group of part.split(':')) {
      if (!/^[0-9a-fA-F]{1,4}$/.test(group)) return null
      out.push(parseInt(group, 16))
    }
    return out
  }

  const head = toGroups(halves[0])
  const tail = halves.length === 2 ? toGroups(halves[1]) : []
  if (!head || !tail) return null

  const slots = embedded ? 6 : 8
  let groups: number[]
  if (halves.length === 2) {
    // '::' must stand in for at least one group.
    if (head.length + tail.length > slots - 1) return null
    const gap = slots - head.length - tail.length
    groups = [...head, ...new Array(gap).fill(0), ...tail]
  } else {
    if (head.length + tail.length !== slots) return null
    groups = [...head, ...tail]
  }
  if (embedded) {
    groups.push(
      (embedded[0] << 8) | embedded[1],
      (embedded[2] << 8) | embedded[3]
    )
  }

  const bytes: number[] = []
  for (const group of groups) bytes.push(group >> 8, group & 0xff)
  return bytes
}

/** True when a 4-byte IPv4 address is globally routable. */
function isPublicIpv4(b: number[]): boolean {
  const [a, second, third] = b
  if (a === 0) return false // 0.0.0.0/8 this network
  if (a === 10) return false // RFC1918
  if (a === 127) return false // loopback
  if (a === 100 && second >= 64 && second <= 127) return false // CGNAT (Alibaba metadata)
  if (a === 169 && second === 254) return false // link-local incl. 169.254.169.254
  if (a === 172 && second >= 16 && second <= 31) return false // RFC1918
  if (a === 192 && second === 0 && third === 0) return false // IETF protocol assignments
  if (a === 192 && second === 0 && third === 2) return false // TEST-NET-1
  if (a === 192 && second === 88 && third === 99) return false // 6to4 relay anycast
  if (a === 192 && second === 168) return false // RFC1918
  if (a === 198 && (second === 18 || second === 19)) return false // benchmarking
  if (a === 198 && second === 51 && third === 100) return false // TEST-NET-2
  if (a === 203 && second === 0 && third === 113) return false // TEST-NET-3
  if (a >= 224) return false // multicast, reserved, broadcast
  return true
}

/** True when a 16-byte IPv6 address is globally routable. */
function isPublicIpv6(b: number[]): boolean {
  const allZero = b.every(byte => byte === 0)
  if (allZero) return false // ::
  if (b.slice(0, 15).every(byte => byte === 0) && b[15] === 1) return false // ::1

  const first12Zero = b.slice(0, 12).every(byte => byte === 0)
  // ::ffff:0:0/96 (IPv4-mapped) and the deprecated ::/96 IPv4-compatible form
  // both carry a real IPv4 destination in the last four bytes.
  if (
    b.slice(0, 10).every(byte => byte === 0) &&
    b[10] === 0xff &&
    b[11] === 0xff
  ) {
    return isPublicIpv4(b.slice(12))
  }
  if (first12Zero) return isPublicIpv4(b.slice(12))

  // 64:ff9b::/96 NAT64 also wraps an IPv4 destination.
  if (b[0] === 0x00 && b[1] === 0x64 && b[2] === 0xff && b[3] === 0x9b) {
    return isPublicIpv4(b.slice(12))
  }
  // 100::/64 discard-only
  if (b[0] === 0x01 && b[1] === 0x00 && b.slice(2, 8).every(x => x === 0)) {
    return false
  }
  // 2001:db8::/32 documentation
  if (b[0] === 0x20 && b[1] === 0x01 && b[2] === 0x0d && b[3] === 0xb8) {
    return false
  }
  // 2002::/16 6to4 embeds an arbitrary IPv4 address
  if (b[0] === 0x20 && b[1] === 0x02) return false
  if ((b[0] & 0xfe) === 0xfc) return false // fc00::/7 unique local
  if (b[0] === 0xfe && (b[1] & 0xc0) === 0x80) return false // fe80::/10 link-local
  if (b[0] === 0xfe && (b[1] & 0xc0) === 0xc0) return false // fec0::/10 site-local
  if (b[0] === 0xff) return false // ff00::/8 multicast
  return true
}

/**
 * True when `input` is an IP literal in any textual form. Brackets and zone
 * ids are tolerated so this can be fed a raw `URL.hostname`.
 */
export function isIpLiteral(input: string): boolean {
  const host = stripBrackets(input)
  return parseIpv4(host) !== null || parseIpv6(host) !== null
}

/**
 * True only when `input` is an IP literal that is safe to connect to.
 *
 * Anything that is not parseable as an IP returns false: callers use this on
 * addresses that came back from DNS, where an unparseable answer means
 * something is wrong and we fail closed.
 */
export function isPublicIpAddress(input: string): boolean {
  if (typeof input !== 'string') return false
  const host = stripBrackets(input.trim())
  const v4 = parseIpv4(host)
  if (v4) return isPublicIpv4(v4)
  const v6 = parseIpv6(host)
  if (v6) return isPublicIpv6(v6)
  return false
}

/**
 * Validate and sanitize a URL for use as an HTML href attribute.
 *
 * Scheme allowlist (default: https, http, mailto, tel). Relative paths
 * starting with a single `/` and fragment links starting with `#` are
 * passed through (no scheme injection possible). Reconstructs absolute
 * URLs via the URL constructor + toString() so the input string never
 * flows directly into the DOM, eliminating DOM-XSS via
 * javascript:/data:/vbscript: schemes.
 *
 * Returns a normalized URL string if safe, or null if rejected.
 */
const SAFE_HOSTNAME_RE = /^[a-zA-Z0-9.-]+$/

export type AllowedScheme = 'https:' | 'http:' | 'mailto:' | 'tel:'

const DEFAULT_ALLOWED_SCHEMES: AllowedScheme[] = [
  'https:',
  'http:',
  'mailto:',
  'tel:',
]

export function sanitizeHref(
  input: string,
  options: { allowedSchemes?: AllowedScheme[] } = {}
): string | null {
  if (typeof input !== 'string') return null
  const trimmed = input.trim()
  if (trimmed.length === 0) return null

  // Relative path (single leading `/`, not protocol-relative `//`)
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed
  // Fragment link
  if (trimmed.startsWith('#')) return trimmed

  const allowed = options.allowedSchemes ?? DEFAULT_ALLOWED_SCHEMES
  try {
    const url = new URL(trimmed)
    if (!allowed.includes(url.protocol as AllowedScheme)) return null
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      if (!SAFE_HOSTNAME_RE.test(url.hostname)) return null
    }
    return url.toString()
  } catch {
    return null
  }
}

/**
 * Validate an email address shape for use as the suffix of `mailto:${email}`.
 * Rejects whitespace, control characters, and characters that could break
 * out of the URI (`<`, `>`, `"`, `'`, `,`, `;`, `?`, `&`).
 *
 * Returns the trimmed email if valid, or null if rejected.
 */
const EMAIL_RE = /^[^\s@<>"',;?&]+@[^\s@<>"',;?&]+\.[a-zA-Z]{2,}$/

export function sanitizeEmail(input: string): string | null {
  if (typeof input !== 'string') return null
  const trimmed = input.trim()
  if (trimmed.length === 0 || trimmed.length > 254) return null
  if (!EMAIL_RE.test(trimmed)) return null
  return trimmed
}

/**
 * Validate a bare domain shape for use as the suffix of `https://${domain}`.
 * Rejects already-prefixed URLs (anything containing `://`), paths,
 * whitespace, and invalid hostname characters.
 *
 * Returns the lower-cased domain if valid, or null if rejected.
 */
const DOMAIN_RE =
  /^(?!-)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.(?!-)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

export function sanitizeDomain(input: string): string | null {
  if (typeof input !== 'string') return null
  const trimmed = input.trim().toLowerCase()
  if (trimmed.length === 0 || trimmed.length > 253) return null
  if (
    trimmed.includes('://') ||
    trimmed.includes('/') ||
    trimmed.includes('@') ||
    /\s/.test(trimmed)
  )
    return null
  if (!DOMAIN_RE.test(trimmed)) return null
  return trimmed
}

/**
 * Canonicalize loosely-typed domain input down to a bare hostname.
 *
 * `sanitizeDomain` deliberately rejects anything that is not already a bare
 * hostname. This is its companion for the entry points where humans (or a
 * model echoing them) hand us a whole URL: chat pastes, the `?domain=` seed,
 * the free-check field. Parsing with `URL` rather than a chain of regexes is
 * what strips path, query, fragment, port, and credentials correctly.
 *
 *   "example.com"                      → "example.com"
 *   "https://example.com/p?a=1#f"      → "example.com"
 *   "example.com?ref=x"                → "example.com"
 *   "javascript://evil.com"            → null   (non-http scheme)
 *
 * Returns the lower-cased hostname, or null if it is not a usable domain.
 */
export function canonicalizeDomainInput(input: string): string | null {
  if (typeof input !== 'string') return null
  const trimmed = input.trim()
  if (trimmed.length === 0 || trimmed.length > 2048) return null

  let hostname: string
  try {
    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
    const url = new URL(hasScheme ? trimmed : `https://${trimmed}`)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    hostname = url.hostname
  } catch {
    return null
  }

  // The URL parser normalizes structure; sanitizeDomain is still the single
  // authority on what counts as a valid hostname shape.
  return sanitizeDomain(hostname)
}

/**
 * Structural check that a URL is safe to fetch (SSRF prevention).
 *
 * Enforces the scheme allowlist, the port allowlist, the hostname denylist,
 * and — when the host is a literal IP — that the address is globally
 * routable. Every textual IP form the WHATWG URL parser normalizes
 * (decimal `2130706433`, hex `0x7f000001`, short `127.1`) arrives here as a
 * canonical dotted-quad, and bracketed IPv6 is unwrapped before classifying.
 *
 * This is necessary but NOT sufficient: a public-looking hostname can still
 * resolve to a private address. Only `fetchExternal` closes that, by
 * resolving DNS and validating every answer. Prefer `fetchExternal` for
 * anything that actually performs a request.
 *
 * Returns the parsed URL if valid, or null if rejected.
 */
export function validateExternalUrl(input: string): URL | null {
  let url: URL
  try {
    url = new URL(input)
  } catch {
    return null
  }
  if (!ALLOWED_PROTOCOLS.has(url.protocol)) return null
  if (!ALLOWED_PORTS.has(url.port)) return null

  const host = stripBrackets(url.hostname).toLowerCase()
  if (host.length === 0) return null
  if (BLOCKED_HOSTNAMES.has(host)) return null
  if (host.endsWith('.localhost')) return null

  // A literal IP can be classified now; a name needs DNS (see fetchExternal).
  if (isIpLiteral(host) && !isPublicIpAddress(host)) return null

  return url
}

// fetchExternal — the guarded fetch that pairs with validateExternalUrl —
// lives in sanitize-node.ts. It needs node:dns to resolve and vet every
// destination, and this module is in the client bundle (ten client
// components import sanitizeHref/sanitizeDomain from here), so it cannot
// be re-exported from this file at any cost: Turbopack refuses a node:
// builtin in a client chunk, even behind a dynamic import.
